/**
 * Simple cookie consent for the Dentrist vendor portal.
 * Stores an "accepted" flag; optional scripts may run only after accept.
 */

export const CONSENT_STORAGE_KEY = 'dentrist-cookie-consent';
export const CONSENT_CHANGED_EVENT = 'dentrist:cookie-consent-changed';

const isBrowser = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const emitConsentChanged = () => {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT));
};

export const hasAcceptedConsent = () => {
  if (!isBrowser()) {
    return false;
  }

  try {
    return window.localStorage.getItem(CONSENT_STORAGE_KEY) === 'accepted';
  } catch {
    return false;
  }
};

export const needsConsentPrompt = () => !hasAcceptedConsent();

/** Essential always allowed; optional categories require Accept. */
export const hasCategoryConsent = (categoryId) => {
  if (categoryId === 'essential') {
    return true;
  }
  return hasAcceptedConsent();
};

export const acceptConsent = () => {
  if (isBrowser()) {
    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, 'accepted');
    } catch {
      // Storage may be unavailable; still emit so UI can dismiss for this session.
    }
  }
  emitConsentChanged();
};

/**
 * Subscribe to consent updates in this tab and across tabs (storage event).
 * Returns an unsubscribe function.
 */
export const subscribeConsentChange = (listener) => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const onCustom = () => {
    listener(hasAcceptedConsent());
  };

  const onStorage = (event) => {
    if (event.key === CONSENT_STORAGE_KEY || event.key === null) {
      listener(hasAcceptedConsent());
    }
  };

  window.addEventListener(CONSENT_CHANGED_EVENT, onCustom);
  window.addEventListener('storage', onStorage);

  return () => {
    window.removeEventListener(CONSENT_CHANGED_EVENT, onCustom);
    window.removeEventListener('storage', onStorage);
  };
};
